import { InputAdornment } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import {
  Loader,
  OnboardingWrapper,
  VisibilityIcon,
} from '../../shared/components';

type ViewProps = {
  isLoading: boolean;
  email?: string;
  handleLogin: (email: string, password: string) => void;
  requestDynamicLink: () => void;
  loginError: boolean;
};

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: '19px',
    fontWeight: 600,
    lineHeight: '28.5px',
    letterSpacing: '0.03px',
    marginBottom: '20px',
    color: theme.palette.primary.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  textFieldsWrapper: {
    padding: '0 14px',
  },
  textField: {
    marginBottom: '30px',
    width: '100%',
    '& label': {
      fontSize: '13px',
    },
    '& input': {
      color: theme.palette.text.secondary,
    },
  },
  textFieldPassword: {
    marginBottom: '10px',
  },
  forgetPassword: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: '17px',
    color: theme.palette.text.secondary,
    marginLeft: '5%',
    marginBottom: '30px',
    cursor: 'pointer',
  },
  passwordContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  button: {
    width: '100%',
    marginBottom: '15px',
    cursor: 'pointer',
    fontSize: '17px',
    '&:disabled': {
      backgroundColor: '#92C6C1',
      color: 'white',
    },
  },
  noAccountWrapper: {
    display: 'flex',
    color: theme.palette.text.secondary,
    flexWrap: 'wrap',
    '& p': {
      fontSize: '13px',
    },
  },
  requestDynamicLink: {
    marginLeft: '4px',
    color: theme.palette.primary.main,
    fontWeight: 700,
    cursor: 'pointer',
  },
}));

const validationSchema = Yup.object().shape({
  password: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email address').required('Required'),
});

const View: React.FC<ViewProps> = ({
  email,
  isLoading,
  handleLogin,
  requestDynamicLink,
  loginError,
}) => {
  const classes = useStyles();
  const initialValues = {
    email: email || '',
    password: '',
  };
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const onSubmit = () => {
    const { email, password } = values;
    handleLogin(email.toLowerCase(), password);
  };

  const { errors, values, handleSubmit, handleChange, isValid } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (values !== initialValues) {
      setIsFormValid(isValid);
    }
  }, [isValid, values]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <OnboardingWrapper
      error={
        loginError ? 'You have entered an invalid email or password' : null
      }>
      <>
        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.textFieldsWrapper}>
            <Typography className={classes.header}>Log in</Typography>
            <TextField
              className={classes.textField}
              id="email"
              name="email"
              label="Email"
              type="email"
              helperText={errors.email}
              error={Boolean(errors.email)}
              variant="outlined"
              onChange={handleChange}
              value={values.email}
            />
            <TextField
              className={`${classes.textField} ${classes.textFieldPassword}`}
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              helperText={errors.password}
              error={Boolean(errors.password)}
              variant="outlined"
              onChange={handleChange}
              value={values.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setShowPassword(!showPassword)}>
                    <VisibilityIcon visible={showPassword} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography className={classes.forgetPassword}>
              Forgot password?
            </Typography>
          </div>
          <Button
            disabled={!isFormValid}
            color="primary"
            variant="contained"
            type="submit"
            className={classes.button}>
            Continue
          </Button>
          <div className={classes.noAccountWrapper}>
            <Typography>Don’t have an account?</Typography>
            <Typography
              className={classes.requestDynamicLink}
              onClick={requestDynamicLink}>
              Request a link
            </Typography>
          </div>
        </form>
      </>
    </OnboardingWrapper>
  );
};

export default View;
